import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, errMsg, AUTHED_FLAG } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        // Skip the /auth/me call for users who have never authenticated in
        // this browser. The flag is non-sensitive (just a boolean) — the
        // actual session lives in a httpOnly cookie that JS can't read.
        if (!sessionStorage.getItem(AUTHED_FLAG) && !localStorage.getItem(AUTHED_FLAG)) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            // Cookie expired or revoked. Clear stale flags and surface in dev.
            if (e?.response?.status !== 401) {
                console.warn("[auth] /me failed:", errMsg(e));
            }
            sessionStorage.removeItem(AUTHED_FLAG);
            localStorage.removeItem(AUTHED_FLAG);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        sessionStorage.setItem(AUTHED_FLAG, "1");
        localStorage.setItem(AUTHED_FLAG, "1");
        setUser(data.user);
        return data.user;
    }, []);

    const register = useCallback(async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        sessionStorage.setItem(AUTHED_FLAG, "1");
        localStorage.setItem(AUTHED_FLAG, "1");
        setUser(data.user);
        return data.user;
    }, []);

    const adminLogin = useCallback(async (username, password) => {
        const { data } = await api.post("/auth/admin/login", { username, password });
        sessionStorage.setItem(AUTHED_FLAG, "1");
        localStorage.setItem(AUTHED_FLAG, "1");
        setUser(data.user);
        return data.user;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch (e) {
            console.warn("[auth] logout request failed:", errMsg(e));
        }
        sessionStorage.removeItem(AUTHED_FLAG);
        localStorage.removeItem(AUTHED_FLAG);
        setUser(null);
    }, []);

    const value = useMemo(
        () => ({ user, loading, login, register, adminLogin, logout, refresh, errMsg }),
        [user, loading, login, register, adminLogin, logout, refresh]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
